/**
 * Like/dislike control for one object (a question or an AI-feedback block).
 *
 * Like is a one-tap toggle (no panel). Dislike opens an inline reason panel.
 * Like and dislike are mutually exclusive and both untoggle-able. On an eligible
 * AI-feedback dislike the human-review offer card is shown beneath the row.
 *
 * @module RatingRow
 */
import { useState } from 'react';
import { Check, ChevronRight, Flag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { deleteRating, postRating, type RatingDetail } from '@/lib/ratingsApi';
import type { RatingModule, RatingTargetType, RatingValue } from '@/lib/ratingConstants';
import { DislikePanel } from './DislikePanel';
import { HumanReviewOfferCard } from './HumanReviewOfferCard';
import type { ReviewModule } from '@/lib/humanReviewsApi';

export interface RatingRowProps {
  readonly targetType: RatingTargetType;
  readonly targetId: string;
  readonly module: RatingModule;
  readonly initialRating?: RatingDetail | null;
}

export function RatingRow({ targetType, targetId, module, initialRating }: RatingRowProps) {
  const { t } = useTranslation();
  const [rating, setRating] = useState<RatingValue | null>(initialRating?.rating ?? null);
  const [ratingId, setRatingId] = useState<string | null>(initialRating?.id ?? null);
  const [reasonCodes, setReasonCodes] = useState<string[]>(initialRating?.reasonCodes ?? []);
  const [freeText, setFreeText] = useState<string>(initialRating?.freeText ?? '');
  const [panelOpen, setPanelOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [offerVisible, setOfferVisible] = useState(false);

  const isQuestion = targetType === 'question';
  const likeLabel = isQuestion ? t('rating.likeQuestionLabel') : t('rating.likeFeedbackLabel');
  const dislikeLabel = isQuestion ? t('rating.dislikeQuestionLabel') : t('rating.dislikeFeedbackLabel');
  const likeAria = isQuestion ? t('rating.likeQuestionAria') : t('rating.likeFeedbackAria');
  const dislikeAria = isQuestion ? t('rating.dislikeQuestionAria') : t('rating.dislikeFeedbackAria');
  const groupLabel = isQuestion ? t('rating.rateQuestion') : t('rating.rateFeedback');

  const handleLike = async () => {
    setPanelOpen(false);
    setOfferVisible(false);
    if (rating === 'like') {
      // Untoggle.
      const id = ratingId;
      setRating(null);
      setRatingId(null);
      if (id) {
        const ok = await deleteRating(id);
        if (!ok) {
          setRating('like');
          setRatingId(id);
        }
      }
      return;
    }
    // Optimistic switch to like.
    const previous = { rating, ratingId };
    setRating('like');
    setReasonCodes([]);
    setFreeText('');
    setIsSaving(true);
    const result = await postRating({ targetType, targetId, module, rating: 'like' });
    setIsSaving(false);
    if (result) {
      setRatingId(result.id);
    } else {
      setRating(previous.rating);
      setRatingId(previous.ratingId);
    }
  };

  const handleDislikeButton = () => {
    setOfferVisible(false);
    if (rating === 'dislike' && !panelOpen) {
      // Re-open prefilled panel for an existing dislike.
      setPanelOpen(true);
      return;
    }
    setPanelOpen((open) => !open);
  };

  const handleDislikeSubmit = async (codes: string[], text: string) => {
    setIsSaving(true);
    const result = await postRating({
      targetType,
      targetId,
      module,
      rating: 'dislike',
      reasonCodes: codes,
      freeText: text || undefined,
    });
    setIsSaving(false);
    if (!result) {
      toast.error(t('rating.saveError'));
      return;
    }
    setRating('dislike');
    setRatingId(result.id);
    setReasonCodes(codes);
    setFreeText(text);
    setPanelOpen(false);
    setOfferVisible(result.offerHumanReview);
  };

  const isLiked = rating === 'like';
  const isDisliked = rating === 'dislike';

  return (
    <div className="border-t border-border/50 pt-2">
      <div
        className="flex items-center gap-2"
        role="group"
        aria-label={groupLabel}
      >
        <span className="font-ui text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
          {isQuestion ? t('rating.questionLabel') : t('rating.feedbackLabel')}
        </span>
        <button
          type="button"
          aria-pressed={isLiked}
          aria-label={likeAria}
          onClick={handleLike}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60',
            isLiked
              ? 'border-success/40 bg-success/10 text-success'
              : 'border-border bg-background text-muted-foreground hover:border-success/30 hover:text-foreground',
          )}
        >
          <Check className="h-3 w-3" aria-hidden />
          {likeLabel}
        </button>
        <button
          type="button"
          aria-pressed={isDisliked}
          aria-label={dislikeAria}
          aria-expanded={panelOpen}
          onClick={handleDislikeButton}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60',
            isDisliked
              ? 'border-destructive/40 bg-destructive/10 text-destructive'
              : 'border-border bg-background text-muted-foreground hover:border-destructive/30 hover:text-foreground',
          )}
        >
          {isQuestion
            ? <Flag className="h-3 w-3" aria-hidden />
            : null}
          {dislikeLabel}
          {!isDisliked && !isQuestion && <ChevronRight className="h-3 w-3" aria-hidden />}
        </button>
      </div>

      {panelOpen && (
        <DislikePanel
          targetType={targetType}
          module={module}
          initialReasonCodes={isDisliked ? reasonCodes : []}
          initialFreeText={isDisliked ? freeText : ''}
          isSaving={isSaving}
          onSubmit={handleDislikeSubmit}
          onCancel={() => setPanelOpen(false)}
        />
      )}

      {offerVisible && (targetType === 'ai_feedback') && (module === 'writing' || module === 'speaking') && (
        <HumanReviewOfferCard
          targetAnswerId={targetId}
          module={module as ReviewModule}
          ratingId={ratingId ?? undefined}
        />
      )}
    </div>
  );
}
